import { FormDropdown } from '@src/common/components/FormDropdown';
import { Stack } from '@src/common/components/Stack';
import { theme } from '@src/common/styles/Theme';
import type { CollapseListener } from '@src/orders/CollapseListener';
import { EditOrderCollapsible } from '@src/orders/EditOrderCollapsible';
import type { FormEntries } from '@src/orders/FormEntries';
import { useSectionError } from '@src/orders/hooks/useSectionError';
import { ConditionalField } from '@src/orders/PermitFormSections/ConditionalField';
import { ControllerFormNumberInput } from '@src/orders/PermitFormSections/ControllerFormNumberInput';
import { ControllerFormCheckbox } from '@src/orders/PermitFormSections/FormCheckbox';
import { FormDatePicker } from '@src/orders/PermitFormSections/FormDatePicker';
import { useDisabledFields } from '@src/orders/PermitFormSections/useDisabledFields';
import {
  TractorLicenseEffectiveDateField,
  TractorLicenseExpirationDateField,
  tractorLicenseFields,
  TractorLicenseIsApportionedPlateField,
  TractorLicensePlateTypeField,
  TractorLicensePlateYearField,
} from '@src/permitField/TractorLicenseFields';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type TractorLicensePlate = FormEntries<string[]>;

type Props = { fields: string[] } & CollapseListener;

export const TractorLicenseSection = (props: Props) => {
  const { t } = useTranslation();
  const { control } = useFormContext<TractorLicensePlate>();
  const { isFieldDisabled } = useDisabledFields();

  const { sectionInError } = useSectionError(Object.keys(tractorLicenseFields));

  if (props.fields.length <= 0) {
    return null;
  }

  return (
    <EditOrderCollapsible
      title={t('tractorLicenseDetails')}
      id="tractor-license"
      collapseEventName={props.collapseEventName}
      uncollapseEventName={props.uncollapseEventName}
      inError={sectionInError}
    >
      <Stack gap={theme.spacing['3']}>
        <Stack direction="column" gap={theme.spacing['3']}>
          <ConditionalField
            fields={props.fields}
            conditions={TractorLicenseEffectiveDateField.fieldName}
          >
            <FormDatePicker
              isUTC
              control={control}
              fieldName={TractorLicenseEffectiveDateField.fieldName}
              disabled={isFieldDisabled(
                TractorLicenseEffectiveDateField.fieldName,
              )}
            />
          </ConditionalField>
          <ConditionalField
            fields={props.fields}
            conditions={TractorLicenseExpirationDateField.fieldName}
          >
            <FormDatePicker
              isUTC
              control={control}
              fieldName={TractorLicenseExpirationDateField.fieldName}
              disabled={isFieldDisabled(
                TractorLicenseExpirationDateField.fieldName,
              )}
            />
          </ConditionalField>
          <ConditionalField
            fields={props.fields}
            conditions={TractorLicensePlateTypeField.fieldName}
          >
            <FormDropdown
              control={control}
              fieldName={TractorLicensePlateTypeField.fieldName}
              options={TractorLicensePlateTypeField.options.map((option) => ({
                value: option,
                label: option,
              }))}
              disabled={isFieldDisabled(TractorLicensePlateTypeField.fieldName)}
            />
          </ConditionalField>
          <ConditionalField
            fields={props.fields}
            conditions={TractorLicensePlateYearField.fieldName}
          >
            <ControllerFormNumberInput
              control={control}
              fieldName={TractorLicensePlateYearField.fieldName}
              disabled={isFieldDisabled(TractorLicensePlateYearField.fieldName)}
            />
          </ConditionalField>
          <ConditionalField
            fields={props.fields}
            conditions={TractorLicenseIsApportionedPlateField.fieldName}
          >
            <ControllerFormCheckbox
              control={control}
              fieldName={TractorLicenseIsApportionedPlateField.fieldName}
              disabled={isFieldDisabled(
                TractorLicenseIsApportionedPlateField.fieldName,
              )}
            />
          </ConditionalField>
        </Stack>
      </Stack>
    </EditOrderCollapsible>
  );
};
